import { applyTheme, getStoredTheme, onThemeChange } from "./theme";

/**
 * Тонкая обёртка над window.Telegram.WebApp (перенесена из peptide-shop).
 *
 * Всё, что зависит от рантайма Telegram, живёт здесь — так приложение
 * открывается и в обычном браузере (для отладки вёрстки), просто без
 * авторизации и без нативных кнопок.
 */
interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { start_param?: string };
  colorScheme: "light" | "dark";
  /** android | android_x | ios | macos | tdesktop | weba | webk | unknown */
  platform: string;
  version: string;
  isFullscreen?: boolean;
  ready(): void;
  expand(): void;
  /** Bot API 8.0+, только мобильные клиенты. */
  requestFullscreen?(): void;
  isVersionAtLeast?(version: string): boolean;
  /** Хекс принимают с Bot API 6.9. */
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  onEvent(event: string, handler: (payload?: unknown) => void): void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const webApp = (): TelegramWebApp | null => window.Telegram?.WebApp ?? null;

/** Пусто в обычном браузере. */
export const getInitData = (): string => webApp()?.initData ?? "";

/**
 * Есть ли подписанный контекст Telegram. Проверяется наличие initData, а не
 * объекта WebApp: скрипт создаёт window.Telegram.WebApp и в обычном
 * браузере, только initData там пустой.
 */
export const hasTelegramContext = (): boolean => getInitData().length > 0;

/** Параметр из ссылки t.me/<бот>/<app>?startapp=... */
export const getStartParam = (): string | null =>
  webApp()?.initDataUnsafe?.start_param ?? null;

const MOBILE_PLATFORMS = ["android", "android_x", "ios"];

/**
 * Мобильный ли клиент — по platform из Telegram, а не по ширине окна:
 * Mini App в Telegram Desktop открывается в узком окне с телефон шириной.
 */
export function isMobilePlatform(): boolean {
  const app = webApp();
  if (app?.platform) return MOBILE_PLATFORMS.includes(app.platform);
  return window.matchMedia("(pointer: coarse)").matches;
}

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Красит шапку и фон клиента под текущую тему. Цвет берём из уже
 * применённой CSS-переменной --bg: палитра живёт в theme.css, и дубль
 * хекса здесь однажды разъехался бы с ней.
 */
function paintClientChrome(): void {
  const app = webApp();
  if (!app || app.isVersionAtLeast?.("6.9") !== true) return;

  const styles = getComputedStyle(document.documentElement);
  const bg = styles.getPropertyValue("--bg").trim();
  const surface = styles.getPropertyValue("--surface").trim();
  if (!HEX.test(bg)) return;

  try {
    app.setHeaderColor?.(bg);
    app.setBackgroundColor?.(bg);
    if (HEX.test(surface)) app.setBottomBarColor?.(surface);
  } catch {
    /* клиент не умеет — остаёмся с его цветом */
  }
}

export function initTelegram(): void {
  const app = webApp();
  const stored = getStoredTheme();

  // Подписываемся ДО первого applyTheme: тот же вызов покрасит шапку на старте.
  onThemeChange(paintClientChrome);

  if (!app) {
    applyTheme(stored ?? "light");
    return;
  }

  app.ready();
  app.expand();

  /**
   * Полноэкранный режим (Bot API 8.0). Только на мобильных: на десктопе
   * метод либо отсутствует, либо разворачивает окно на весь монитор.
   */
  if (isMobilePlatform() && typeof app.requestFullscreen === "function") {
    try {
      app.requestFullscreen();
    } catch {
      /* старый клиент */
    }
  }

  const syncFullscreen = () => {
    document.documentElement.dataset.fullscreen = app.isFullscreen ? "1" : "0";
  };
  syncFullscreen();
  app.onEvent("fullscreenChanged", syncFullscreen);
  app.onEvent("fullscreenFailed", syncFullscreen);

  // Telegram перекрашивает свою шапку при смене темы клиента — возвращаем нашу.
  app.onEvent("themeChanged", paintClientChrome);

  applyTheme(stored ?? "light");
}

export function haptic(style: "light" | "medium" = "light"): void {
  webApp()?.HapticFeedback?.impactOccurred(style);
}

export function hapticNotify(type: "error" | "success" | "warning"): void {
  webApp()?.HapticFeedback?.notificationOccurred(type);
}

/**
 * Внешняя страница (оплата) — во внешнем браузере через openLink: внутри
 * WebView у неё не было бы кнопки «назад», а Mini App остаётся открытым
 * и показывает заказ.
 */
export function openExternalLink(url: string): void {
  const app = webApp();
  if (app) app.openLink(url);
  else window.open(url, "_blank", "noopener");
}

/** Канал и поддержка открываются в самом Telegram. */
export function openTelegramLink(url: string): void {
  const app = webApp();
  if (app) app.openTelegramLink(url);
  else window.open(url, "_blank", "noopener");
}
